/**
 * StripeOAuthReturnHandler — global handler that intercepts the post-OAuth
 * landing on `/project/{pid}/integrations?stripe=connected&mode=X` and
 * bounces the user back to their editor URL (when one was stashed in
 * sessionStorage by the paywall picker).
 *
 * Renders null. Mount once near the project route root.
 *
 * Why this exists: the backend OAuth callback redirects to a HARDCODED
 * `/project/{pid}/integrations` URL. Today the Integrations page is the
 * canonical entry point for connect — but M7 added an alternative entry
 * from inside the V2 screen editor's paywall product picker. The picker
 * writes a return URL to sessionStorage before redirecting; this handler
 * reads it on the next mount and navigates back via React Router.
 *
 * Coexistence with the Integrations page's own `ConnectStripeButton`
 * detection (which fires `onConnected` from its useEffect on URL params):
 * the Integrations page button writes `stripe_oauth_in_progress` and
 * `stripe_oauth_mode` keys; our return URL is at a DIFFERENT key
 * (`segmently_stripe_oauth_return`). No conflict. If our key is absent,
 * we no-op and the Integrations page handles the post-OAuth UX itself.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { getReturnUrl, clearReturnUrl, type OAuthReturnEntry } from '../utils/oauthReturnUrl';

const LOG_PREFIX = '[StripeOAuthReturnHandler]';

/**
 * Extract `?stripe=connected&mode=test|live` from a search string.
 * Returns null when either param is missing or invalid.
 */
function parseOAuthReturnParams(search: string): {
  stripe: string;
  mode: 'test' | 'live';
} | null {
  const params = new URLSearchParams(search);
  const stripe = params.get('stripe');
  const mode = params.get('mode');
  if (stripe !== 'connected') return null;
  if (mode !== 'test' && mode !== 'live') return null;
  return { stripe, mode };
}

/**
 * Capture URL params + return entry synchronously at first render. Using
 * a useRef lazy initializer means this runs BEFORE any effects in the tree
 * — including the Integrations page's `ConnectStripeButton` URL-stripping
 * effect. That guarantees we always see the params Stripe sent us, even
 * if a sibling component cleans them up moments later.
 *
 * Also reads from `window.location.search` directly as a fallback in case
 * React Router's `useLocation()` hasn't initialized yet on the very first
 * render of a route.
 */
function captureInitialState(initialSearch: string) {
  const params =
    parseOAuthReturnParams(initialSearch) ||
    parseOAuthReturnParams(window.location.search);
  const returnEntry: OAuthReturnEntry | null = getReturnUrl();
  return { params, returnEntry };
}

export const StripeOAuthReturnHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Guard against double-fire from React StrictMode or routing churn —
  // we only handle the return once per app session lifecycle.
  const handledRef = useRef(false);
  // CRITICAL: capture URL params + sessionStorage entry at the FIRST RENDER
  // via useRef lazy init. This runs synchronously before any sibling
  // useEffect can fire, eliminating any race condition with components
  // that strip URL params on mount (e.g. ConnectStripeButton's own
  // useEffect inside the Integrations page).
  const initialStateRef = useRef(captureInitialState(location.search));
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMode, setToastMode] = useState<'test' | 'live' | null>(null);

  useEffect(() => {
    if (handledRef.current) return;

    // Prefer the snapshot captured during initial render (race-resistant).
    // If that didn't have what we need, retry against the live location +
    // sessionStorage — handles the case where the user navigates to the
    // return URL after the component has already mounted.
    const initial = initialStateRef.current;
    const params =
      initial.params ||
      parseOAuthReturnParams(location.search) ||
      parseOAuthReturnParams(window.location.search);
    const returnEntry: OAuthReturnEntry | null =
      initial.returnEntry ?? getReturnUrl();

    // eslint-disable-next-line no-console
    console.info(LOG_PREFIX, 'detect attempt', {
      pathname: location.pathname,
      searchFromRouter: location.search,
      searchFromWindow: window.location.search,
      paramsFound: !!params,
      paramsValue: params,
      returnEntryFound: !!returnEntry,
      returnEntryShape: returnEntry
        ? { url: returnEntry.url, mode: returnEntry.mode, expiresIn: returnEntry.expiresAt - Date.now() }
        : null,
    });

    if (!params || !returnEntry) return;

    // Mark handled BEFORE side effects so a re-render during navigate
    // can't cause a double-bounce.
    handledRef.current = true;

    // Clear the stash so a refresh on the destination URL doesn't
    // re-trigger us.
    clearReturnUrl();

    // Surface a toast on the destination route confirming the connect.
    setToastMode(params.mode);
    setToastOpen(true);

    // eslint-disable-next-line no-console
    console.info(LOG_PREFIX, 'navigating back to editor', returnEntry.url);

    // Navigate back. We deliberately use `replace: true` so the user's
    // browser Back button skips the Integrations bounce. The page they
    // navigate TO is the picker's stashed URL — typically the canvas or
    // the V2 screen editor route.
    navigate(returnEntry.url, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return (
    <Snackbar
      open={toastOpen}
      autoHideDuration={6000}
      onClose={() => setToastOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="success" onClose={() => setToastOpen(false)} sx={{ width: '100%' }}>
        {toastMode === 'live'
          ? 'Live Stripe account connected — reopen the paywall to continue.'
          : 'Sandbox Stripe account connected — reopen the paywall to continue.'}
      </Alert>
    </Snackbar>
  );
};

StripeOAuthReturnHandler.displayName = 'StripeOAuthReturnHandler';
